import React, { useState } from "react";
import { Image, Pressable, StyleSheet, Text, View } from "react-native";
import { CF } from "../../theme/turquoiseTheme.js";
import atomicHabitsCover from "../../assets/library/atomic_habits_cover.png";
import dopamineNationCover from "../../assets/library/dopamine_nation_cover.png";

const LOCAL_COVERS = {
  book_atomic_habits: atomicHabitsCover,
  book_dopamine_nation: dopamineNationCover,
};

/**
 * Local artwork first (reliable on web). Remote Open Library URLs often fail in the browser.
 * @param {{ id?: string, coverUrl?: string } | null | undefined} book
 */
export function getBookCoverSource(book) {
  if (!book) return null;
  if (book.id && LOCAL_COVERS[book.id]) return LOCAL_COVERS[book.id];
  if (typeof book.coverUrl === "string" && book.coverUrl.trim()) return { uri: book.coverUrl };
  return null;
}

/**
 * Book card with a large cover preview (same idea as video thumbnails).
 * @param {{
 *   title: string,
 *   author: string,
 *   blurb?: string,
 *   coverUrl?: string,
 *   bookId?: string,
 *   featured?: boolean,
 *   onPress: () => void,
 * }} props
 */
export function BookCoverCard({ title, author, blurb, coverUrl, bookId, featured, onPress }) {
  const [imgFailed, setImgFailed] = useState(false);
  const source = getBookCoverSource({ id: bookId, coverUrl });
  const showCover = !!source && !imgFailed;

  return (
    <Pressable style={styles.card} onPress={onPress}>
      <View style={styles.coverWrap}>
        {showCover ? (
          <Image
            source={source}
            style={styles.cover}
            resizeMode="cover"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <View style={[styles.cover, styles.coverFallback]}>
            <Text style={styles.coverFallbackIcon}>📖</Text>
            <Text style={styles.coverFallbackTitle} numberOfLines={3}>
              {title}
            </Text>
          </View>
        )}
      </View>
      <View style={styles.meta}>
        <Text style={styles.title} numberOfLines={2}>
          {title}
        </Text>
        <Text style={styles.author} numberOfLines={1}>
          {author}
        </Text>
        {blurb ? (
          <Text style={styles.blurb} numberOfLines={2}>
            {blurb}
          </Text>
        ) : null}
        {featured ? <Text style={styles.badge}>Top pick</Text> : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    marginHorizontal: 16,
    marginBottom: 14,
    borderRadius: 16,
    overflow: "hidden",
    backgroundColor: "rgba(6, 12, 28, 0.72)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
  },
  coverWrap: {
    width: "100%",
    height: 220,
    backgroundColor: "#0F172A",
    alignItems: "center",
    justifyContent: "center",
  },
  cover: {
    width: "100%",
    height: "100%",
  },
  coverFallback: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    backgroundColor: "#134E4A",
  },
  coverFallbackIcon: { fontSize: 36, marginBottom: 10 },
  coverFallbackTitle: {
    color: "#ECFDF5",
    fontWeight: "800",
    fontSize: 16,
    textAlign: "center",
  },
  meta: { padding: 12 },
  title: { color: "#FFFFFF", fontWeight: "800", fontSize: 16, marginBottom: 4 },
  author: { color: CF.cyan, fontWeight: "700", fontSize: 12, marginBottom: 6 },
  blurb: { color: "#CBD5E1", fontSize: 13, lineHeight: 18 },
  badge: {
    alignSelf: "flex-start",
    marginTop: 10,
    fontSize: 10,
    fontWeight: "800",
    color: "#042F2E",
    backgroundColor: CF.cyan,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    overflow: "hidden",
  },
});
